
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchResultHighlight } from "./SearchResultHighlight";
import ProgressIndicator from "@/components/gamification/ProgressIndicator";
import { useSearch } from "@/hooks/useSearch";
import { useAuth } from "@/hooks/useAuth";
import { Clock, User, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";

interface SearchResultsProps {
  searchQuery: string;
  filters: {
    discipline?: string;
    status?: string;
    nodeType?: string;
    tags?: string[];
    dateRange?: {
      from?: Date;
      to?: Date;
    };
    sortBy?: string;
  };
}

export const SearchResults = ({ searchQuery, filters }: SearchResultsProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;
  const { user } = useAuth();

  const { data: results, isLoading, error } = useSearch(
    searchQuery,
    filters,
    pageSize,
    currentPage * pageSize
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-black">Error loading search results</p>
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="text-center py-8">
        <BookOpen className="h-12 w-12 mx-auto text-black mb-4" />
        <h3 className="text-lg font-semibold mb-2">No results found</h3>
        <p className="text-black">
          {searchQuery 
            ? `No content found for "${searchQuery}"`
            : "No published content available"
          }
        </p>
      </div>
    );
  }

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (results.length === pageSize) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-black">
          {searchQuery ? (
            <>
              Search results for "<SearchResultHighlight text={searchQuery} searchQuery={searchQuery} className="font-medium" />"
            </>
          ) : (
            "Recent content"
          )}
        </p>
        <p className="text-sm text-black">
          Page {currentPage + 1} • {results.length} results
        </p>
      </div>

      <div className="space-y-4">
        {results.map((result) => (
          <Card key={result.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg mb-2">
                    <Link 
                      to={`/node/${result.id}`}
                      className="hover:text-primary transition-colors"
                    >
                      <SearchResultHighlight 
                        text={result.title} 
                        searchQuery={searchQuery}
                      />
                    </Link>
                  </CardTitle>
                  <div className="flex items-center gap-4 text-sm text-black">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <SearchResultHighlight 
                        text={result.author_full_name || result.author_username}
                        searchQuery={searchQuery}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {format(new Date(result.published_at || result.updated_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant="secondary">
                    {result.node_type}
                  </Badge>
                  {result.discipline_name && (
                    <Badge variant="outline">
                      <SearchResultHighlight 
                        text={result.discipline_name}
                        searchQuery={searchQuery}
                      />
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {result.content?.preview && (
                  <p className="text-black line-clamp-3">
                    <SearchResultHighlight 
                      text={result.content.preview}
                      searchQuery={searchQuery}
                    />
                  </p>
                )}
                
                {/* Progress Indicator for authenticated users */}
                {user && (
                  <ProgressIndicator 
                    nodeId={result.id} 
                    variant="icon" 
                    showTimeSpent={true}
                    className="border-t pt-3"
                  />
                )}
                
                {result.tags && result.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {result.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <SearchResultHighlight 
                          text={tag}
                          searchQuery={searchQuery}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevPage}
          disabled={currentPage === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-black px-4">
          Page {currentPage + 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={results.length < pageSize}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
